import { cn } from "@-/com.npmjs.cn";
import {
  WebsiteCard,
  WebsiteContainer,
  WebsiteEyebrow,
  WebsiteHeading,
  WebsiteSection,
  WebsiteText,
} from "@-/dev.improve.haniverse.components.website";
import { LandingGlyphFormula, type LandingGlyph } from "#landing-glyph-tile";
import { LandingMnemonicStoryText } from "#landing-mnemonic-story-text";

type LandingCurriculumStep = {
  /** The lesson's position in Level 1 of the Simplified course. */
  lesson: number;
  parts: LandingGlyph[];
  result: LandingGlyph;
};

const STICK: LandingGlyph = { glyph: "丨", label: "stick" };
const CEILING: LandingGlyph = { glyph: "￣", label: "ceiling" };
const POT: LandingGlyph = { glyph: "凵", label: "pot" };
const MOUNTAIN: LandingGlyph = { glyph: "山", label: "mountain" };
const CROSS: LandingGlyph = { glyph: "十", label: "cross" };
const DRY: LandingGlyph = { glyph: "干", label: "dry" };
const CLIFF: LandingGlyph = { glyph: "厂", label: "cliff" };

/** Every lesson of Level 1 that 岸 (shore), the level's last character, stands on. */
const LANDING_SHORE_CURRICULUM_STEPS: LandingCurriculumStep[] = [
  { lesson: 3, parts: [{ glyph: "𠃊", label: "chair" }, STICK], result: POT },
  { lesson: 4, parts: [POT, STICK], result: MOUNTAIN },
  { lesson: 9, parts: [{ glyph: "一", label: "line" }, STICK], result: CROSS },
  { lesson: 11, parts: [CEILING, CROSS], result: DRY },
  { lesson: 16, parts: [CEILING, { glyph: "丿", label: "throw" }], result: CLIFF },
  { lesson: 20, parts: [MOUNTAIN, CLIFF, DRY], result: { glyph: "岸", label: "shore" } },
];

const LANDING_SHORE_MNEMONIC =
  "Picture Han on a [_mountain_](山) [_cliff_](厂), sitting on [_dry_](干) land and admiring the [**shore**](.) far below.";

function LandingCurriculumStepRow({
  isLast,
  step,
}: {
  isLast: boolean;
  step: LandingCurriculumStep;
}) {
  return (
    <li
      className={cn(
        "grid gap-x-6 gap-y-3 border-t border-black/8 px-5 py-4 sm:grid-cols-[5.5rem_minmax(0,1fr)] sm:items-center sm:px-8",
        isLast && "bg-website-accent/6",
      )}
    >
      <span
        className={cn(
          "text-xs font-semibold tracking-wide uppercase tabular-nums",
          isLast ? "text-website-accent" : "text-black/40",
        )}
      >
        Lesson {step.lesson}
      </span>
      <LandingGlyphFormula parts={step.parts} result={step.result} />
      {isLast ? (
        <WebsiteText className="sm:col-start-2" size="caption" tone="default">
          <LandingMnemonicStoryText mnemonic={LANDING_SHORE_MNEMONIC} />
        </WebsiteText>
      ) : null}
    </li>
  );
}

/**
 * The second promise: lessons only ever use what earlier lessons taught. Level 1's path from single
 * strokes to 岸 (shore) shows the whole idea in six lessons.
 */
export function LandingCurriculumSection() {
  return (
    <WebsiteSection id="lessons">
      <WebsiteContainer className="grid items-start gap-12 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-20">
        <div className="lg:sticky lg:top-10">
          <WebsiteEyebrow>Lessons that build</WebsiteEyebrow>
          <WebsiteHeading className="mt-4 max-w-[14ch]" level={2}>
            Nothing shows up before its parts do.
          </WebsiteHeading>
          <WebsiteText className="mt-5 max-w-[30rem]" size="lead">
            Haniverse orders its lessons so each new character is made only from pieces you’ve
            already learned. Every step is a small one, and you spend it combining what you know
            instead of memorizing yet another shape from scratch.
          </WebsiteText>
          <div className="mt-10 border-l-2 border-website-accent pl-5">
            <p className="m-0 text-[2.5rem] leading-none font-semibold tracking-[-0.04em]">
              <span lang="zh-Hans">岸</span>{" "}
              <span className="text-[1.5rem] text-black/40">HSK 6</span>
            </p>
            <WebsiteText className="mt-3 max-w-[26rem]">
              “Shore” is an HSK 6 character, yet it closes your very first level. By lesson 20 it’s
              only three things you already know: mountain, cliff and dry.
            </WebsiteText>
          </div>
        </div>

        <WebsiteCard className="overflow-hidden" padding="none">
          <div className="px-5 pt-6 pb-5 sm:px-8 sm:pt-7">
            <WebsiteEyebrow tone="muted">
              Level 1 · Strokes, components and primitives
            </WebsiteEyebrow>
            <WebsiteHeading className="mt-2" level={3} size="title">
              From one stroke to 岸 in twenty lessons
            </WebsiteHeading>
          </div>
          <ol className="m-0 list-none p-0">
            {LANDING_SHORE_CURRICULUM_STEPS.map((step, index) => (
              <LandingCurriculumStepRow
                isLast={index === LANDING_SHORE_CURRICULUM_STEPS.length - 1}
                key={step.lesson}
                step={step}
              />
            ))}
          </ol>
        </WebsiteCard>
      </WebsiteContainer>
    </WebsiteSection>
  );
}
